import clsx from "clsx";
import React from "react";
import { ABSENCE_STATUS, HEALTH_ABSENCE_TYPE, TRANSFER_STATUS } from "../constants";
import type { AbsenceData2 } from "../types";

export interface CellClassResult {
  changeClass: string;
  icon: React.ReactNode;
  absenceDataForCell: AbsenceData2 | null;
}

export function getAbsenceForDate(
  absences: AbsenceData2[] | undefined,
  formattedDate: string,
): AbsenceData2 | null {
  if (!absences) return null;

  return absences.find((absence) => {
    if (Number(absence.confirmed) === ABSENCE_STATUS.REJECTED) return false;
    const from = absence.fromDate.split("T")[0];
    const to = absence.toDate.split("T")[0];
    return formattedDate >= from && formattedDate <= to;
  }) ?? null;
}

export function getColorClass(absence: AbsenceData2): string {
  const confirmed = Number(absence.confirmed ?? 0);
  const transferred = Number(absence.transferred ?? 1);

  // Health absence (type 8)
  if (
    (transferred === TRANSFER_STATUS.FROM_OPTIMA && absence.type === HEALTH_ABSENCE_TYPE) ||
    (transferred === TRANSFER_STATUS.FROM_HR && absence.type === HEALTH_ABSENCE_TYPE && confirmed === ABSENCE_STATUS.HR_CONFIRMED)
  ) {
    return "bg-[#ad4ae2] text-white";
  }

  // Optima absence (non-health)
  if (transferred === TRANSFER_STATUS.FROM_OPTIMA && absence.type !== HEALTH_ABSENCE_TYPE) {
    return "bg-[#a8ffa0] text-black";
  }

  // HR-created absences
  switch (confirmed) {
    case ABSENCE_STATUS.REJECTED:
      return "dx-calendar-cell-work";
    case ABSENCE_STATUS.UNCONFIRMED:
      return "dx-calendar-cell-unconfirmed";
    case ABSENCE_STATUS.MANAGER_CONFIRMED:
      return "dx-calendar-cell-manager-confirmed";
    case ABSENCE_STATUS.HR_CONFIRMED:
    case ABSENCE_STATUS.CANCELLED:
      return "dx-calendar-cell-hr-confirmed";
    default:
      return "";
  }
}

export function getAbsenceIcon(absence: AbsenceData2): React.ReactNode {
  const confirmed = Number(absence.confirmed ?? 0);
  const synch = Number(absence.synch ?? 0);
  const transferred = Number(absence.transferred ?? 1);

  if (transferred === TRANSFER_STATUS.FROM_OPTIMA) {
    return React.createElement("i", { className: "dx-icon-database", style: { color: "gray" } });
  }

  if (confirmed === ABSENCE_STATUS.UNCONFIRMED || confirmed === ABSENCE_STATUS.MANAGER_CONFIRMED) {
    return React.createElement("i", { className: "dx-icon-close", style: { color: "red" } });
  }

  if (confirmed === ABSENCE_STATUS.HR_CONFIRMED) {
    if (synch === 1) {
      return React.createElement("i", { className: "dx-icon-todo", style: { color: "lightgreen" } });
    }
    return React.createElement("i", { className: "dx-icon-clock", style: { color: "black" } });
  }

  if (confirmed === ABSENCE_STATUS.CANCELLED) {
    return React.createElement("i", { className: "dx-icon-clear text-white" });
  }

  if (transferred === TRANSFER_STATUS.FROM_HR) {
    return React.createElement("i", { className: "dx-icon-check", style: { color: "green" } });
  }

  return null;
}

export function getCellClassAndIcon(
  formattedDate: string,
  theme: string | undefined,
  employeeId: number,
  holidayData: Record<number, AbsenceData2[]>,
  dayType: number | null,
): CellClassResult {
  const absences = holidayData[employeeId] || [];
  const absenceDataForCell = getAbsenceForDate(absences, formattedDate);

  if (!absenceDataForCell) {
    return {
      changeClass: clsx({
        "dx-calendar-cell-work": dayType === 1,
        "dx-calendar-cell-free": dayType === 2,
        "dx-calendar-cell-holiday": dayType === 3,
        "dx-calendar-cell-udefind": dayType === null,
        "dx-calendar-cell-dark": theme === "dark",
      }),
      icon: null,
      absenceDataForCell: null,
    };
  }

  const colorClass = getColorClass(absenceDataForCell);
  const icon = getAbsenceIcon(absenceDataForCell);

  return {
    changeClass: clsx(colorClass, { "dx-calendar-cell-dark": theme === "dark" }),
    icon,
    absenceDataForCell,
  };
}

export interface BorrowedBlockInfo {
  isBorrowedAndBlocked: boolean;
  isMyBorrowedEmployee: boolean;
}

export function getBorrowedBlockStatus(
  employee: {
    isBorrowed: boolean;
    borrowedByManagerId?: number | null;
    borrowedByManagerCompanyId?: number | null;
    startDateOfTheRental?: string | null;
    endDateOfTheRental?: string | null;
  },
  dayDate: string,
  currentUserId: number | undefined,
  currentUserCompanyId: number | undefined,
  isHR: boolean,
): BorrowedBlockInfo {
  if (!employee.isBorrowed || isHR) {
    return { isBorrowedAndBlocked: false, isMyBorrowedEmployee: false };
  }

  const isMyBorrowedEmployee =
    employee.borrowedByManagerId === currentUserId &&
    employee.borrowedByManagerCompanyId === currentUserCompanyId;

  const rentalStart = employee.startDateOfTheRental ? new Date(employee.startDateOfTheRental) : null;
  const rentalEnd = employee.endDateOfTheRental ? new Date(employee.endDateOfTheRental) : null;
  const currentDate = new Date(dayDate);

  const isInRentalPeriod =
    rentalStart !== null &&
    rentalEnd !== null &&
    currentDate >= rentalStart &&
    currentDate <= rentalEnd;

  let isBorrowedAndBlocked: boolean;
  if (isMyBorrowedEmployee) {
    isBorrowedAndBlocked = !isInRentalPeriod;
  } else {
    isBorrowedAndBlocked = isInRentalPeriod;
  }

  return { isBorrowedAndBlocked, isMyBorrowedEmployee };
}
